import { useEffect } from 'react';

import { RootState } from '@iwProvider/configureStore';
import { H1 } from '@ui';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import AccountDetails from './accountDetails/AccountDetails';
import { SUBMIT_COMPANY_DETAILS } from './companyDetails/CompanyDetails.actions';
import type { CompanyDetailsFormValues } from './companyDetails/CompanyDetails.types';
import { CompanyDetailsForm } from './companyDetails/CompanyDetailsForm';
import PaymentDetails from './paymentDetails/PaymentDetails';
import People from './people/People';
import {
    ACTIVATE_PROFILE,
    CANCEL_PROMPT,
    LEAVE_PROFILE,
    OPEN_PROMPT
} from './Profile.actions';
import { useTrackPageView } from '../../tracking/pageView';
import MainContentPending from '../pageStructure/mainContentPending/MainContentPending';

type ProfileSlice = {
    ready: boolean;
    openPrompt: boolean;
    submitCompanyDetailsLoading?: boolean;
    company_name?: string;
    company_number?: string;
    trading_name?: string;
    companyType?: { value: string; label: string };
    website?: string;
    annual_turnover?: string;
    annual_profit?: string;
    trading_from?: { day: string; month: string; year: string };
    industry?: string;
};

const mapStateToProps = (state: RootState): ProfileSlice => state.profile;

const mapDispatchToProps = (dispatch: Dispatch) => ({
    activate: () => dispatch({ type: ACTIVATE_PROFILE }),
    leave: () => dispatch({ type: LEAVE_PROFILE }),
    promptSave: () => dispatch({ type: OPEN_PROMPT }),
    cancelPrompt: () => dispatch({ type: CANCEL_PROMPT }),
    submitForm: (companyDetails: CompanyDetailsFormValues) =>
        dispatch({ type: SUBMIT_COMPANY_DETAILS, companyDetails })
});

type PropsFromRedux = ProfileSlice & ReturnType<typeof mapDispatchToProps>;

const Profile = ({
    activate,
    leave,
    cancelPrompt,
    promptSave,
    submitForm,
    ready,
    openPrompt,
    submitCompanyDetailsLoading,
    company_name,
    company_number,
    trading_name,
    companyType,
    website,
    annual_turnover,
    annual_profit,
    trading_from,
    industry
}: PropsFromRedux) => {
    useTrackPageView({ name: 'Profile' });

    useEffect(() => {
        activate();
        return () => {
            leave();
        };
    }, [activate, leave]);

    if (!ready) return <MainContentPending data-testid="profile-loading" />;

    const initialValues: CompanyDetailsFormValues = {
        companyName: company_name ?? '',
        companyNumber: company_number ?? '',
        tradingName: trading_name ?? '',
        companyType: companyType?.label ?? '',
        website: website ?? '',
        annualTurnover: annual_turnover ?? '',
        annualProfit: annual_profit ?? '',
        tradingFrom: trading_from ?? { day: '', month: '', year: '' },
        industry: industry ?? ''
    };

    return (
        <div className="mx-auto max-w-5xl space-y-2xl px-s py-xl">
            <H1>
                <FormattedMessage
                    id="_.your_company.title"
                    defaultMessage="Profile"
                />
            </H1>

            <People />

            <PaymentDetails />

            <CompanyDetailsForm
                initialValues={initialValues}
                companyType={companyType ?? { value: '', label: '' }}
                openPrompt={openPrompt}
                submitting={Boolean(submitCompanyDetailsLoading)}
                onSubmit={submitForm}
                onPromptOpen={promptSave}
                onPromptCancel={cancelPrompt}
            />

            <AccountDetails />
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(Profile);
